import type { AdmissionRequirement } from '@backrs_ui/common-ui';

export const USER_COLLEGE_STATUSES = [
  'interested',
  'applying',
  'applied',
  'accepted',
  'waitlisted',
  'denied',
  'enrolled',
] as const;
export type UserCollegeStatus = (typeof USER_COLLEGE_STATUSES)[number];

export const APPLICATION_PLANS = ['ed', 'ea', 'rea', 'rd', 'rolling'] as const;
export type ApplicationPlan = (typeof APPLICATION_PLANS)[number];

export const COLLEGE_TIERS = ['safety', 'target', 'reach'] as const;
export type CollegeTier = (typeof COLLEGE_TIERS)[number];

export const COLLEGE_TYPES = ['public', 'private'] as const;
export type CollegeType = (typeof COLLEGE_TYPES)[number];

export const COLLEGE_PROGRAMS = ['four_year', 'community_college', 'trade_school'] as const;
export type CollegeProgram = (typeof COLLEGE_PROGRAMS)[number];

type NetCostSource = 'award_letter' | 'scorecard' | 'average';

export type UserCollege = Readonly<{
  id: number;
  institution_id: number;
  academic_profile_id: number;
  ipeds_id: number | null;
  name: string;
  type: CollegeType | null;
  level: CollegeProgram | null;
  logo_media_url: string | null;
  banner_media_url: string | null;
  status: UserCollegeStatus;
  application_plan: ApplicationPlan | null;
  created_at: string;
  application_deadline: string | null;
  early_decision: boolean | null;
  early_decision_deadline: string | null;
  early_action: boolean | null;
  early_action_restrictive: boolean | null;
  early_action_deadline: string | null;
  notification_date: string | null;
  rolling_admission: boolean | null;
  test_optional: boolean | null;
  test_blind: boolean;
  application_fee: number | null;
  application_fee_waiver: boolean | null;
  essay_requirement: AdmissionRequirement | null;
  recommendation_requirement: AdmissionRequirement | null;
  interview_requirement: AdmissionRequirement | null;
  my_deadline: string | null;
  has_award_letter: boolean;
  net_cost: number | null;
  net_cost_source: NetCostSource | null;
  estimated_net_cost: number | null;
  tier: CollegeTier | null;
}>;

export type CollegePick = Readonly<{
  institution_id: number;
  name: string;
  logo_media_url: string | null;
}>;

export type UserCollegesResponse = Readonly<{
  has_scores: boolean;
  has_family_income: boolean;
  colleges: readonly UserCollege[];
}>;

export type AddUserCollegePayload = { institution_id: number } | { ipeds_id: number };

export type UpdateUserCollegePayload = Readonly<{
  id: number;
  status?: UserCollegeStatus;
  application_plan?: ApplicationPlan | null;
}>;

export const SCHOLARSHIP_RENEWAL_ANSWERS = ['same_amount', 'keep_gpa', 'no', 'unknown'] as const;
export type ScholarshipRenewal = (typeof SCHOLARSHIP_RENEWAL_ANSWERS)[number];

export const AWARD_AMOUNT_FIELDS = [
  'cost_of_attendance',
  'grants',
  'scholarships',
  'loans',
  'work_study',
] as const;
export type AwardAmountField = (typeof AWARD_AMOUNT_FIELDS)[number];

export type AwardAmounts = Readonly<Record<AwardAmountField, number | null>>;

export type AwardLetter = AwardAmounts &
  Readonly<{
    user_college_id: number;
    scholarships_renewable: ScholarshipRenewal | null;
    net_cost: number | null;
    media: Readonly<{ id: number; name: string | null }> | null;
  }>;

export type SaveAwardLetterPayload = AwardAmounts &
  Readonly<{
    userCollegeId: number;
    scholarships_renewable: ScholarshipRenewal | null;
    media: AwardLetter['media'];
  }>;

export type ExtractedAwardLetter = AwardAmounts &
  Readonly<{
    scholarships_renewable: Exclude<ScholarshipRenewal, 'unknown'> | null;
    cost_of_attendance_stated: boolean;
    loans_counted_as_aid: boolean;
    parent_plus_listed_as_aid: boolean;
  }>;

export const COMPARE_FACTORS = [
  'net_cost',
  'graduation_rate',
  'median_earnings',
  'roi_10_year',
  'distance',
  'type',
  'size',
  'setting',
] as const;
export type CompareFactor = (typeof COMPARE_FACTORS)[number];

export const DISTANCE_OPTIONS = ['weekends', 'few_hours', 'flight', 'anywhere'] as const;
export type DistanceOption = (typeof DISTANCE_OPTIONS)[number];

export const PREFERENCE_OPTIONS = {
  type: ['public', 'private'],
  size: ['small', 'medium', 'large'],
  setting: ['urban', 'suburban', 'small_town', 'rural'],
} as const;
export type PreferenceFactor = keyof typeof PREFERENCE_OPTIONS;
export type PreferenceOption = (typeof PREFERENCE_OPTIONS)[PreferenceFactor][number];

export const MAX_COMPARISON_COLLEGES = 5;

export type ComparisonSettings = Readonly<{
  importances: Readonly<Record<CompareFactor, number>>;
  distance: DistanceOption;
  preferences: Readonly<{
    [Factor in PreferenceFactor]: Readonly<
      Record<(typeof PREFERENCE_OPTIONS)[Factor][number], number>
    >;
  }>;
}>;

export type FitStep = 1 | 2 | 3 | 4 | 5;

type ComparedFactor = Readonly<{ value: number | string | null; fit: FitStep | null }>;

export type ComparedCollege = Readonly<{
  institution_id: number;
  user_college_id: number | null;
  academic_profile_id: number;
  ipeds_id: number | null;
  name: string;
  logo_media_url: string | null;
  banner_media_url: string | null;
  city: string | null;
  state: string | null;
  status: UserCollegeStatus | null;
  net_cost_source: NetCostSource | null;
  factors: Readonly<Record<CompareFactor, ComparedFactor>>;
  match: number | null;
  best_at: CompareFactor | null;
  watch: CompareFactor | null;
}>;

export type ScoredComparison = Readonly<{
  settings: ComparisonSettings;
  weights: Readonly<Record<CompareFactor, number>>;
  has_home: boolean;
  colleges: readonly ComparedCollege[];
}>;

export type CollegeComparison = ScoredComparison & Readonly<{ id: number; name: string }>;

export type ComparisonSummary = Readonly<{
  id: number;
  name: string;
  institution_ids: readonly number[];
  updated_at: string;
}>;

export type ScoreComparisonPayload = Readonly<{
  institution_ids: readonly number[];
  settings?: ComparisonSettings;
}>;

export type CreateComparisonPayload = ScoreComparisonPayload & Readonly<{ name: string }>;

export type UpdateComparisonPayload = Readonly<{
  id: number;
  name?: string;
  settings?: ComparisonSettings;
  institution_ids?: readonly number[];
}>;
